"use client";

import { useEffect, useRef } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import Image from "next/image";
import { shapeAsset } from "@/lib/shapes";
import { useMotion } from "@/contexts/MotionContext";

interface MVPBuildAnimationProps {
  width?: string;
  height?: string;
}

const codeSnippets = [
  { text: "const mvp = new Product({", color: "#71D9C5" },
  { text: "  speed: 'lightning',", color: "#4681D1", indent: 1 },
  { text: "  quality: 'exceptional',", color: "#F6BBCD", indent: 1 },
  { text: "  design: 'beautiful'", color: "#FFE28A", indent: 1 },
  { text: "})", color: "#71D9C5" },
];

const shapes = [
  { src: shapeAsset("ball", "pink"), delay: 0, x: 20, y: -20 },
  { src: shapeAsset("cube", "pink"), delay: 0.2, x: -30, y: 30 },
  { src: shapeAsset("pyramid", "pink"), delay: 0.4, x: 40, y: 10 },
  { src: shapeAsset("arch", "pink"), delay: 0.6, x: -10, y: -40 },
];

export default function MVPBuildAnimation({
  width = "100%",
  height = "100%",
}: MVPBuildAnimationProps) {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref);
  const { isMotionEnabled } = useMotion();

  useEffect(() => {
    if (isInView && isMotionEnabled) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, isInView, isMotionEnabled]);

  const containerVariants = {
    hidden: {
      transition: {
        staggerChildren: 0,
      },
    },
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 1, x: 0 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
      },
    },
  };

  const cursorVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: [0, 1, 0],
      transition: {
        duration: 0.8,
        repeat: Infinity,
        repeatType: "reverse" as const,
      },
    },
  };

  const shapeVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -180 },
    visible: (delay: number) => ({
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        delay,
        type: "spring",
        bounce: 0.5,
        duration: 1,
      },
    }),
  };

  const floatVariants = {
    initial: () => ({
      x: 0,
      y: 0,
      rotate: 0,
    }),
    animate: ({ x, y }: { x: number; y: number }) => ({
      x,
      y,
      rotate: [0, 15, -15, 0],
      transition: {
        x: {
          duration: 4,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        },
        y: {
          duration: 3,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        },
        rotate: {
          duration: 6,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        },
      },
    }),
  };

  if (!isMotionEnabled) {
    return (
      <div ref={ref} style={{ width, height }} className="relative font-mono">
        <div className="absolute left-0 top-0 w-full h-full bg-gray-900/90 backdrop-blur-sm rounded-xl border border-border-dark p-6">
          <div className="flex gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-brand-red opacity-80" />
            <div className="w-3 h-3 rounded-full bg-brand-yellow opacity-80" />
            <div className="w-3 h-3 rounded-full bg-brand-teal opacity-80" />
          </div>
          <div className="space-y-1">
            {codeSnippets.map((line, index) => (
              <div key={index} className="flex items-center">
                <div
                  style={{
                    marginLeft: `${line.indent ? line.indent * 1 : 0}rem`,
                    color: line.color,
                  }}
                  className="font-mono text-sm md:text-base"
                >
                  {line.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ width, height }} className="relative font-mono">
      {/* Code typing effect */}
      <motion.div
        className="absolute left-0 top-0 w-full h-full bg-gray-900/90 backdrop-blur-sm rounded-xl border border-border-dark p-6"
        variants={containerVariants}
        initial="visible"
        animate={controls}
      >
        <div className="flex gap-2 mb-4">
          <div className="w-3 h-3 rounded-full bg-brand-red opacity-80" />
          <div className="w-3 h-3 rounded-full bg-brand-yellow opacity-80" />
          <div className="w-3 h-3 rounded-full bg-brand-teal opacity-80" />
        </div>

        <div className="space-y-1">
          {codeSnippets.map((line, index) => (
            <motion.div
              key={index}
              className="flex items-center"
              variants={textVariants}
              custom={index}
            >
              <div
                style={{
                  marginLeft: `${line.indent ? line.indent * 1 : 0}rem`,
                  color: line.color,
                }}
                className="font-mono text-sm md:text-base"
              >
                {line.text}
              </div>
              {index === codeSnippets.length - 1 && (
                <motion.div
                  className="w-2 h-4 bg-brand-purple ml-1"
                  variants={cursorVariants}
                />
              )}
            </motion.div>
          ))}
        </div>

        {/* Floating shapes */}
        {shapes.map((shape, index) => (
          <motion.div
            key={index}
            className="absolute w-12 h-12"
            style={{
              top: `${20 + index * 15}%`,
              left: `${60 + index * 10}%`,
            }}
            variants={shapeVariants}
            custom={shape.delay}
            initial="hidden"
            animate={isMotionEnabled ? "visible" : "hidden"}
          >
            <motion.div
              variants={floatVariants}
              custom={{ x: shape.x, y: shape.y }}
              initial="initial"
              animate={isMotionEnabled ? "animate" : "initial"}
              className="w-full h-full relative"
            >
              <Image src={shape.src} alt="" fill className="object-contain" />
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
